import React, { useEffect, useState } from 'react'
import LocaleText from 'components/LocaleText'
import { DriveNodeType } from 'config/driveTypes'
import { formatFileSize, getFileKind, getItemVisual } from 'components/Library/helpers'
import { openInCloudViewer } from 'utils'
import { fileExtension, getFileMetadata, isPreviewableImageFile, loadFileMetadata, safeFileUrl } from './fileMetadata'
import styles from './FileCards.module.css'

const FileCard: React.FC<{ url: string }> = ({ url }) => {
  const [loaded, setLoaded] = useState({ url, metadata: getFileMetadata(url) })
  const [failedImageUrl, setFailedImageUrl] = useState<string>()
  const metadata = loaded.url === url ? loaded.metadata : getFileMetadata(url)
  const node = { type: DriveNodeType.FILE, mime_type: metadata.mime }
  const { Icon, color, bg } = getItemVisual(node)
  const extension = fileExtension(metadata.name).toUpperCase()
  const size = metadata.size === 0 ? '0 B' : formatFileSize(metadata.size)
  const href = safeFileUrl(url)
  const showImage = !!href && isPreviewableImageFile(metadata) && failedImageUrl !== url
  const Tag = href ? 'a' : 'div'

  useEffect(() => {
    let active = true
    loadFileMetadata(url).then((metadata) => {
      if (active) setLoaded({ url, metadata })
    })
    return () => {
      active = false
    }
  }, [url])

  return (
    <Tag
      className={styles.card}
      href={href ? openInCloudViewer(href, metadata.name) : undefined}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      title={metadata.name}
    >
      <span className={styles.icon} style={{ color, background: bg }} aria-hidden="true">
        {showImage ? <img src={href} alt="" onError={() => setFailedImageUrl(url)} /> : <Icon size={24} />}
      </span>
      <span className={styles.content}>
        <bdi className={styles.name}>{metadata.name}</bdi>
        <span className={styles.meta}>
          <LocaleText text={getFileKind(node)} />
          {extension && (
            <>
              {' '}
              · <bdi>{extension}</bdi>
            </>
          )}
          {size && (
            <>
              {' '}
              · <bdi>{size}</bdi>
            </>
          )}
        </span>
      </span>
    </Tag>
  )
}

export const FileCards: React.FC<{ files: string[] }> = ({ files }) => (
  <div className={styles.list}>
    {files.map((url) => (
      <FileCard key={url} url={url} />
    ))}
  </div>
)
